import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  BillingAccountsForUserResponse,
  getUserBillingAccounts
} from '~/billing/services/billingService'
import { useUserContext } from './UserContext'

// Local storage key prefix for selected billing account
const SELECTED_BILLING_ACCOUNT_KEY_PREFIX = 'selectedBillingAccountId'

// Helper function to get user-specific localStorage key
const getUserSpecificKey = (userId: number | undefined): string | null => {
  if (!userId) return null
  return `${SELECTED_BILLING_ACCOUNT_KEY_PREFIX}_${userId}`
}

// Type for billing accounts data that will be shared
export interface BillingAccountsData {
  billingAccounts: BillingAccountsForUserResponse[] | null
  selectedBillingAccountId: number | null
  setSelectedBillingAccountId: (id: number | null) => void
  error: Error | null
  loaded: boolean
  refetch: () => Promise<void>
}

// Create the context
export const BillingAccountsContext = createContext<BillingAccountsData | null>(null)

// Provider component
interface BillingAccountsProviderProps {
  children: ReactNode
}

export function BillingAccountsProvider({ children }: BillingAccountsProviderProps) {
  const [billingAccounts, setBillingAccounts] = useState<BillingAccountsForUserResponse[] | null>(
    null
  )
  const [error, setError] = useState<Error | null>(null)
  const [loaded, setLoaded] = useState<boolean>(false)

  // Get user context to watch for user changes
  const { isAuthenticated, user } = useUserContext()

  // Load selected billing account ID from localStorage
  const [selectedBillingAccountId, setSelectedBillingAccountIdState] = useState<number | null>(
    () => {
      if (typeof window !== 'undefined' && user?.user_id) {
        const key = getUserSpecificKey(user.user_id)
        if (key) {
          const saved = localStorage.getItem(key)
          return saved ? parseInt(saved, 10) : null
        }
      }
      return null
    }
  )

  // Function to set selected billing account ID and save to localStorage
  const setSelectedBillingAccountId = useCallback(
    (id: number | null) => {
      setSelectedBillingAccountIdState(id)
      if (typeof window !== 'undefined') {
        const key = getUserSpecificKey(user?.user_id)
        if (key) {
          if (id !== null) {
            localStorage.setItem(key, id.toString())
          } else {
            localStorage.removeItem(key)
          }
        }
      }
    },
    [user?.user_id]
  )

  const fetchBillingAccounts = useCallback(async () => {
    // Only fetch if user is authenticated and email is verified
    // Skip billing fetch for unverified users to avoid 403 errors
    if (!isAuthenticated || user?.email_verified !== true) {
      setLoaded(true) // Mark as loaded so UI doesn't wait forever
      return
    }
    setLoaded(false)
    setError(null)

    try {
      const accounts = await getUserBillingAccounts()
      setBillingAccounts(accounts)
      setLoaded(true)
    } catch (err) {
      console.warn('Failed to fetch billing accounts:', err)
      setError(err instanceof Error ? err : new Error('Unknown billing accounts error'))
      setLoaded(true)
    }
  }, [isAuthenticated, user?.email_verified])

  // Reset selection and fetch billing accounts when user changes
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear selection when user logs out
      setSelectedBillingAccountIdState(null)
      return
    }

    // Load user-specific selection from localStorage when user changes
    if (typeof window !== 'undefined' && user?.user_id) {
      const key = getUserSpecificKey(user.user_id)
      if (key) {
        const saved = localStorage.getItem(key)
        setSelectedBillingAccountIdState(saved ? parseInt(saved, 10) : null)
      }
    }

    fetchBillingAccounts()
  }, [isAuthenticated, user?.user_id, fetchBillingAccounts])

  // Auto-select first billing account if none is selected OR if selected account is not in the list
  useEffect(() => {
    if (billingAccounts && billingAccounts.length > 0) {
      // Check if current selection is null or not in the available accounts list
      const isSelectionValid =
        selectedBillingAccountId !== null &&
        billingAccounts.some(account => account.id === selectedBillingAccountId)

      if (!isSelectionValid) {
        // Select the first available account as default
        setSelectedBillingAccountId(billingAccounts[0].id)
      }
    }
  }, [billingAccounts, selectedBillingAccountId, setSelectedBillingAccountId])

  const contextValue: BillingAccountsData = useMemo(
    () => ({
      billingAccounts,
      selectedBillingAccountId,
      setSelectedBillingAccountId,
      error,
      loaded,
      refetch: fetchBillingAccounts
    }),
    [
      billingAccounts,
      selectedBillingAccountId,
      setSelectedBillingAccountId,
      error,
      loaded,
      fetchBillingAccounts
    ]
  )

  return (
    <BillingAccountsContext.Provider value={contextValue}>
      {children}
    </BillingAccountsContext.Provider>
  )
}

// Hook to use the context
export function useBillingAccounts(): BillingAccountsData {
  const context = useContext(BillingAccountsContext)
  if (!context) {
    throw new Error('useBillingAccounts must be used within a BillingAccountsProvider')
  }
  return context
}

/**
 * Temporarily scopes BillingContext/BillingProvider to a specific billing account id
 * (e.g. super admin viewing another customer's account) without mutating global selection.
 */
export function SuperAdminBillingAccountOverrideProvider({
  billingAccountId,
  children
}: {
  billingAccountId: number
  children: ReactNode
}) {
  const parent = useBillingAccounts()
  const value = useMemo((): BillingAccountsData => {
    const noopSetSelected = (_id: number | null) => {
      // Intentionally no-op: avoid changing the signed-in user's selected billing account.
    }
    return {
      ...parent,
      selectedBillingAccountId: billingAccountId,
      setSelectedBillingAccountId: noopSetSelected
    }
  }, [parent, billingAccountId])

  return <BillingAccountsContext.Provider value={value}>{children}</BillingAccountsContext.Provider>
}
